import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  InputNumber,
  Popover,
  Radio,
  Select,
  Space,
  TimePicker,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';

import { trackEvent } from '../../../../../../helpers/utils/analytics';
import { disabledMinutes, getCountDaysFromPeriod } from '../../../../../../helpers/utils/reports';
import { periodDescription } from '../../../../constants/tooltips';
import { useActions } from '../../../../hooks/useActions';
import useIsAccessEnded from '../../../../hooks/useIsAccessEnded';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

interface IPeriodSelectorProps {
  isCompare: boolean;
  isFixed?: boolean;
  exInterval?: PeriodInterval
}

export type PeriodInterval = 'selectdates' | 'ndaysago' | 'nhoursago' | 'today' | 'yesterday' | 'thisweek' | 'lastweek' | 'thismonth' | 'lastmonth' | 'thisquart' |  'lastquart' | 'lastquart' | 'thisyear'
| 'lastyear' |  'last7days' | 'last14days' | 'last30days' | 'last365days' | 'allthetime'

const defaultInterval = 'selectdates';

const getPeriodWithInterval = (interval: PeriodInterval, start: any, end: any,  nDaysAgo = 7) => {
  let startDate = new Date(); 
  let endDate = new Date();

  switch (interval) {
      case 'today':
          break;
      case 'ndaysago':
          startDate.setDate(endDate.getDate() - nDaysAgo);
          break;

      case 'yesterday':
          startDate.setDate(endDate.getDate() - 1);
          endDate = new Date(startDate);
          break;

      case 'thisweek':
          const firstDayOfWeek = new Date();
          firstDayOfWeek.setDate(endDate.getDate() - endDate.getDay()); // Установить на первый день недели
          startDate = firstDayOfWeek;
          break;

      case 'lastweek':
          const lastWeekStart = new Date();
          lastWeekStart.setDate(endDate.getDate() - endDate.getDay() - 7); // Установить на первый день прошлой недели
          startDate = lastWeekStart;
          endDate = new Date(lastWeekStart);
          endDate.setDate(endDate.getDate() + 6); // Установить на последний день прошлой недели
          break;

      case 'thismonth':
          startDate.setDate(1); // Установить на первый день текущего месяца
          endDate = new Date(+dayjs().endOf('month'));
          break;

      case 'lastmonth':
          startDate.setMonth(endDate.getMonth() - 1);
          startDate.setDate(1); // Установить на первый день прошлого месяца
          endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 1);
          endDate.setDate(0); // Установить на последний день прошлого месяца
          break;

      case 'thisquart':
          const currentMonth = new Date().getMonth();
          const startMonth = currentMonth - (currentMonth % 3); // Начало квартала
          startDate = new Date(new Date().getFullYear(), startMonth, 1);
          endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 3);
          endDate.setDate(0); // Установить на последний день квартала
          break;

      case 'lastquart':
          const lastQuartMonth = new Date().getMonth() - 3;
          startDate = new Date(new Date().getFullYear(), lastQuartMonth - (lastQuartMonth % 3), 1);
          endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 3);
          endDate.setDate(0); // Установить на последний день прошлого квартала
          break;

      case 'thisyear':
          startDate = new Date(new Date().getFullYear(), 0, 1); // Первый день года
          endDate = new Date(new Date().getFullYear() + 1, 0, 0); // Последний день года
          break;

      case 'lastyear':
          startDate = new Date(new Date().getFullYear() - 1, 0, 1); // Первый день прошлого года
          endDate = new Date(new Date().getFullYear(), 0, 0); // Последний день прошлого года
          break;

      case 'last7days':
          startDate.setDate(endDate.getDate() - 7);
          break;

      case 'last14days':
          startDate.setDate(endDate.getDate() - 14);
          break;

      case 'last30days':
          startDate.setDate(endDate.getDate() - 30);
          break;

      case 'last365days':
          startDate.setDate(endDate.getDate() - 365);
          break;
      case 'nhoursago':
          startDate = new Date(endDate.getTime() - nDaysAgo * 60 * 60 * 1000);
          break;
      case 'allthetime':
        startDate = new Date(2000, 0, 1); // 1 января 2000 года
        break;
      default:
        return { startDate: start, endDate: end };
}
return { startDate, endDate };
};

/** Парсит строку времени "HH:mm" в объект {hour, minute, second} */
const parseTimeToObj = (
  timeStr: string | null | undefined,
  fallback: { hour: number; minute: number; second: number }
): { hour: number; minute: number; second: number } => {
  if (!timeStr) return fallback;
  const m = moment(timeStr, 'HH:mm');
  if (!m.isValid()) return fallback;
  return { hour: m.hour(), minute: m.minute(), second: 0 };
};

/** TimePicker с автозакрытием при выборе минут. */
const NTP = ({ value, placeholder, disabled, onChange }: any) => {
  const _val = value ? moment(value, 'HH:mm') : null;
  const [ open, setOpen ] = useState(false);
  const lastMinuteRef = useRef<number | null>(null);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      lastMinuteRef.current = _val ? _val.minute() : null;
    }
  }, [ _val ]);

  const handleSelect = useCallback((val: moment.Moment) => {
    onChange(val);
    const newMinute = val.minute();
    if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
      setOpen(false);
    }
    lastMinuteRef.current = newMinute;
  }, [ onChange ]);

  return (
    <TimePicker
      disabled={disabled}
      placeholder={placeholder}
      format="HH:mm"
      disabledMinutes={disabledMinutes}
      hideDisabledOptions
      showNow={false}
      value={_val}
      open={open}
      onOpenChange={handleOpenChange}
      onSelect={handleSelect}
      style={{ width: 90 }}
    />
  );
};

/** DatePicker с автозакрытием при выборе минут (когда showTime включён). */
const DP = ({ value, type, format, withTime, isCompare, isAccessEnded, onChangeDate }: any) => {
  const _val = value ? moment(value) : null;
  const [ open, setOpen ] = useState(false);
  const lastMinuteRef = useRef<number | null>(null);

  const disabledDate = (current: any) => {
    if (!isAccessEnded) return false;
    return (
      current.isBefore(moment().subtract(7, 'd')) || current.isAfter(moment())
    );
  };

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      lastMinuteRef.current = _val ? _val.minute() : null;
    }
  }, [ _val ]);

  const handleSelect = useCallback((val: moment.Moment) => {
    onChangeDate(val, type);
    const newMinute = val.minute();
    if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
      setOpen(false);
    }
    lastMinuteRef.current = newMinute;
  }, [ type, onChangeDate ]);

  const openProps = withTime ? {
    open,
    onOpenChange: handleOpenChange,
    onSelect: handleSelect,
  } : {};

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <DatePicker
      value={_val}
      onChange={(date: any) => onChangeDate(date, type)}
      format={format}
      showTime={withTime && { format: 'HH:mm' }}
      allowClear={false}
      style={{ width: withTime ? 170 : 120 }}
      disabled={isCompare}
      disabledDate={disabledDate}
      {...openProps}
    />
  );
};

const PeriodSelector: FC<IPeriodSelectorProps> = ({
  isCompare,
  isFixed = false,
  exInterval
}) => {
  const isWillMount = useRef(true);
  const isAccessEnded = useIsAccessEnded();
  const startTime = { hour: 0, minute: 0, second: 0 };
  const endTime = { hour: 23, minute: 59, second: 59 };
  const period = useTypedSelector((state) => state.period);
  const { details } = useTypedSelector((state) => state.details);
  const { currentDashboard } = useTypedSelector((state) => state.reports);

  const { setPeriod, setDetails } = useActions();

  const [ isOpenOutside, setIsOpenOutside ] = useState(false);
  const [ currentDateSelector, setCurrentDateSelector ] = useState(period.select);
  const [ currentDate, setCurrentDate ] = useState<any>({
    start: null,
    end: null,
    interval: exInterval ?? period.interval ?? defaultInterval,
  });
  const [ nDays, setNDays ] = useState(() => ({
    nStart: (period as any).days?.nStart ?? null,
    nEnd: (period as any).days?.nEnd ?? null,
  }));
  const [ nDaysTime, setNDaysTime ] = useState<{ nStartTime: string | null; nEndTime: string | null }>(() => ({
    nStartTime: (period as any).nStartTime ?? null,
    nEndTime: (period as any).nEndTime ?? null,
  }));
  const [ nHours, setNHours ] = useState(() => ({
    nStart: (period as any).hours?.nStart ?? null,
    nEnd: (period as any).hours?.nEnd ?? null,
  }));
  const [ withTime, setWithTime ] = useState(() => !!(period as any).withTime);
  const isHoursMode = currentDate.interval === 'nhoursago';
  const isDaysWithTime = currentDate.interval === 'ndaysago' && nDaysTime.nStartTime != null;
  const format = (isHoursMode || isDaysWithTime || (withTime && currentDate.interval !== 'ndaysago')) ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';

  const nDaysLabel = useMemo(() => {
    let s = nDays.nStart ?? 0;
    let e = nDays.nEnd ?? 0;
    // Fallback: вычисляем из дат, если nDays не задан
    if (!s && !e && (period.interval === 'ndaysago' || period.select === 'ndaysago') && period.start && period.end) {
      const now = moment().startOf('day');
      s = now.diff(moment(period.start).startOf('day'), 'days');
      e = now.diff(moment(period.end).startOf('day'), 'days');
    }
    if (!s && !e) return 'N дней назад';
    if (nDaysTime.nStartTime != null) {
      return `от ${s} с ${nDaysTime.nStartTime} до ${e} по ${nDaysTime.nEndTime || '23:55'} дней назад`;
    }
    return `от ${s} до ${e} дней назад`;
  }, [ nDays, nDaysTime, period.interval, period.select, period.start, period.end ]);

  const nHoursLabel = useMemo(() => {
    const s = nHours.nStart ?? 0;
    const e = nHours.nEnd ?? 0;
    if (!s && !e) return 'N часов назад';
    return `от ${s} до ${e} часов назад`;
  }, [ nHours ]);

  const [ visibleDateModal, setVisibleDateModal ] = useState(false);

  const isCohort = location.pathname.includes('cohort');
  useEffect(() => {
    if(isWillMount.current){
      exInterval = exInterval ?? period.interval;
      if (isCohort) {
        exInterval === 'last30days';
        setPeriod({ ...period, interval: exInterval });
      
      }
      const { startDate, endDate } = getPeriodWithInterval(exInterval as PeriodInterval, period.start, period.end);
      setCurrentDate({ start: startDate, end: endDate, interval: exInterval });
      setCurrentDateSelector(exInterval);
      isWillMount.current = false;
    }else{
      setCurrentDate({ start: period.start, end: period.end, interval: period.interval || defaultInterval });
      setCurrentDateSelector(period.interval || defaultInterval);
    }
    // Синхронизируем nDays/nDaysTime/nHours из Redux period
    if ((period as any).days) {
      setNDays({ nStart: (period as any).days.nStart, nEnd: (period as any).days.nEnd });
    }
    if ((period as any).nStartTime != null) {
      setNDaysTime({ nStartTime: (period as any).nStartTime, nEndTime: (period as any).nEndTime });
    }
    if ((period as any).hours) {
      setNHours({ nStart: (period as any).hours.nStart, nEnd: (period as any).hours.nEnd });
    }
  }, [ period, currentDashboard ]);
  
  const onChangeRadio = (value: PeriodInterval) => {
    trackEvent('period-change', { interval: value, source: 'quick-buttons' });

    const { startDate, endDate } = getPeriodWithInterval(period.interval as PeriodInterval, period.start, period.end);
    const payload = { start: startDate.toString(), end: endDate.toString(), interval: value };
    setCurrentDate({ start: payload.start, end: payload.end, interval: value });

    setCurrentDateSelector('');
    const periodData = {
      start: payload.start,
      end: payload.end,
      interval: payload.interval || period.interval,
      select: '',
    };
    setPeriod({
      ...periodData,
    });
  };

  const onChangeDate = (date: moment.Moment | null, type: string) => {
    if (!date) return;

    let start = currentDate.start ?? currentDashboard?.values?.period.start ?? period.start;
    let end = currentDate.end ?? currentDashboard?.values?.period.end ?? period.end;

    if (type === 'start') {
      if (date.isAfter(end)) {
        start = date;
        end = date;
      } else {
        start = date;
      }
    } else if (type === 'end') {
      if (date.isAfter(start)) {
        end = date;
      } else {
        start = date;
        end = date;
      }
    }

    setCurrentDate({ start: start, end: end, interval: 'selectdates' });
  };
  const onChangeType = (value: PeriodInterval) => {
    trackEvent('period-change', { interval: value, source: 'dropdown' });

    const { startDate, endDate } = getPeriodWithInterval(value, period.start, period.end);
    const payload = { start: startDate.toString(), end: endDate.toString(), interval: value };
    setCurrentDateSelector(value);

    switch (value) {
      case 'selectdates':
        setCurrentDate((prev: any) => ({ ...prev, interval: value }));
        setTimeout(() => setVisibleDateModal(true), 0);
        return;
      case 'ndaysago': {
        setWithTime(false);
        const _nStart = nDays.nStart ?? 0;
        const _nEnd = nDays.nEnd ?? 0;
        const sTime = parseTimeToObj(nDaysTime.nStartTime, startTime);
        const eTime = parseTimeToObj(nDaysTime.nEndTime, endTime);
        let dStart = moment().subtract(_nStart, 'days').set(sTime);
        let dEnd = moment().subtract(_nEnd, 'days').set(eTime);
        if (dStart.isAfter(dEnd)) [dStart, dEnd] = [dEnd, dStart];
        setCurrentDate({
          start: dStart, end: dEnd, interval: 'ndaysago',
          days: { nStart: _nStart, nEnd: _nEnd },
          nStartTime: nDaysTime.nStartTime, nEndTime: nDaysTime.nEndTime,
        });
        setTimeout(() => { setIsOpenOutside(true); setVisibleDateModal(true); }, 0);
        return;
      }
      case 'nhoursago': {
        setWithTime(false);
        const _nStart = nHours.nStart ?? 0;
        const _nEnd = nHours.nEnd ?? 0;
        let hStart = moment().subtract(_nStart, 'hours');
        let hEnd = moment().subtract(_nEnd, 'hours');
        if (hStart.isAfter(hEnd)) [hStart, hEnd] = [hEnd, hStart];
        setCurrentDate({
          start: hStart, end: hEnd, interval: 'nhoursago',
          hours: { nStart: _nStart, nEnd: _nEnd },
        });
        setTimeout(() => { setIsOpenOutside(true); setVisibleDateModal(true); }, 0);
        return;
      }
    }

    if (isCohort) {
      const countDays = getCountDaysFromPeriod(payload);

      const setDetail = (value: string) => {
        setDetails(value);
      };
      switch (details) {
        case 'q':
          if (countDays >= 1095) {
            setDetail('y');
          }
          break;
        case 'm':
          if (countDays >= 365) {
            setDetail('q');
          }
          break;
        case 'w':
          if (countDays >= 180) {
            setDetail('m');
          }
          break;
        case 'd':
          if (countDays >= 180) {
            setDetail('m');
          } else {
            setDetail('w');
          }
          break;
      }
    }

    setCurrentDate({
      start: payload.start,
      end: payload.end,
      interval: payload.interval,
    });
    setPeriod({
      start: payload.start,
      end: payload.end,
      interval: payload.interval,
      select: value,
    });
  };

  const onApply = () => {
    let start = currentDate.start;
    let end = currentDate.end;

    // Пересчитываем даты для ndaysago / nhoursago из актуальных стейтов
    if (currentDate.interval === 'ndaysago') {
      const _nStart = currentDate.days?.nStart ?? nDays.nStart ?? 0;
      const _nEnd = currentDate.days?.nEnd ?? nDays.nEnd ?? 0;
      const sTime = parseTimeToObj(currentDate.nStartTime ?? nDaysTime.nStartTime, startTime);
      const eTime = parseTimeToObj(currentDate.nEndTime ?? nDaysTime.nEndTime, endTime);
      start = moment().subtract(_nStart, 'days').set(sTime);
      end = moment().subtract(_nEnd, 'days').set(eTime);
      if (moment(start).isAfter(end)) { const tmp = start; start = end; end = tmp; }
    }

    if (currentDate.interval === 'nhoursago') {
      const _nStart = currentDate.hours?.nStart ?? nHours.nStart ?? 0;
      const _nEnd = currentDate.hours?.nEnd ?? nHours.nEnd ?? 0;
      start = moment().subtract(_nStart, 'hours');
      end = moment().subtract(_nEnd, 'hours');
      if (moment(start).isAfter(end)) { const tmp = start; start = end; end = tmp; }
    }

    if (isCohort) {
      const momentStart = moment(start);
      const momentEnd = moment(end);
      const countDays = momentEnd.diff(momentStart, 'days');
      const setDetail = (value: string) => {
        setDetails(value);
      };
      if (countDays >= 1095) {
        setDetail('y');
      } else if (countDays >= 730 || countDays >= 365) {
        setDetail('q');
      } else if (countDays >= 180) {
        setDetail('m');
      } else {
        setDetail('w');
      }
    }

    // При режиме с временем: секунды start=00, end=59
    if (withTime && start && end) {
      start = moment(start).set({ second: 0 });
      end = moment(end).set({ second: 59 });
    }

    setCurrentDateSelector('');
    const isTimeMode = withTime || currentDate.interval === 'nhoursago' || (currentDate.interval === 'ndaysago' && (currentDate.nStartTime != null || nDaysTime.nStartTime != null));
    let periodData: any = {
      start,
      end,
      select: currentDate.interval,
      interval: currentDate.interval,
      withTime: isTimeMode,
    };

    if (currentDate.interval === 'ndaysago') {
      periodData.days = currentDate.days || { nStart: nDays.nStart ?? 0, nEnd: nDays.nEnd ?? 0 };
      if (currentDate.nStartTime != null || nDaysTime.nStartTime != null) {
        periodData.nStartTime = currentDate.nStartTime ?? nDaysTime.nStartTime;
        periodData.nEndTime = currentDate.nEndTime ?? nDaysTime.nEndTime;
      }
    }

    if (currentDate.interval === 'nhoursago') {
      periodData.hours = currentDate.hours || { nStart: nHours.nStart ?? 0, nEnd: nHours.nEnd ?? 0 };
    }

    setCurrentDate(periodData);
    setPeriod({
      ...periodData,
    });
    setVisibleDateModal(false);
  };

  const onChangeN = (type: string, value: number) => {
    let _nStart = nDays.nStart ? nDays.nStart : 0;
    let _nEnd = nDays.nEnd ? nDays.nEnd : 0;
    if (type === 'nStart') _nStart = value;
    if (type === 'nEnd') _nEnd = value;
    let start = moment();
    let end = moment();


    const sTime = parseTimeToObj(nDaysTime.nStartTime, startTime);
    const eTime = parseTimeToObj(nDaysTime.nEndTime, endTime);
    start = start.subtract(_nStart, 'days').set(sTime);
    end = end.subtract(_nEnd, 'days').set(eTime);

    if (start.isAfter(end)) {
      setCurrentDate({
        start: end,
        end: start,
        interval: 'ndaysago',
        days: { nStart: _nStart, nEnd: _nEnd },
        nStartTime: nDaysTime.nStartTime,
        nEndTime: nDaysTime.nEndTime,
      });
    } else {
      setCurrentDate({
        start: start,
        end: end,
        interval: 'ndaysago',
        days: { nStart: _nStart, nEnd: _nEnd },
        nStartTime: nDaysTime.nStartTime,
        nEndTime: nDaysTime.nEndTime,
      });
    }

    setNDays((prevVal) => ({
      ...prevVal,
      [type]: value,
    }));
  };

  const onClickNDaysTime = () => {
    const hasNTime = nDaysTime.nStartTime != null;
    const newTimeState = hasNTime
      ? { nStartTime: null, nEndTime: null }
      : { nStartTime: '00:00', nEndTime: '23:55' };
    setNDaysTime(newTimeState);

    const _nStart = nDays.nStart ?? 0;
    const _nEnd = nDays.nEnd ?? 0;
    const sTime = parseTimeToObj(newTimeState.nStartTime, startTime);
    const eTime = parseTimeToObj(newTimeState.nEndTime, endTime);
    let start = moment().subtract(_nStart, 'days').set(sTime);
    let end = moment().subtract(_nEnd, 'days').set(eTime);
    if (start.isAfter(end)) [start, end] = [end, start];

    setCurrentDate({
      start,
      end,
      interval: 'ndaysago',
      days: { nStart: _nStart, nEnd: _nEnd },
      nStartTime: newTimeState.nStartTime,
      nEndTime: newTimeState.nEndTime,
    });
  };

  const onChangeNDaysTime = (nType: 'nStartTime' | 'nEndTime', val: moment.Moment | null) => {
    const timeStr = val ? val.format('HH:mm') : null;
    const newTimeState = { ...nDaysTime, [nType]: timeStr };
    setNDaysTime(newTimeState);

    const _nStart = nDays.nStart ?? 0;
    const _nEnd = nDays.nEnd ?? 0;
    const sTime = parseTimeToObj(
      nType === 'nStartTime' ? timeStr : nDaysTime.nStartTime, startTime
    );
    const eTime = parseTimeToObj(
      nType === 'nEndTime' ? timeStr : nDaysTime.nEndTime, endTime
    );
    let start = moment().subtract(_nStart, 'days').set(sTime);
    let end = moment().subtract(_nEnd, 'days').set(eTime);
    if (start.isAfter(end)) [start, end] = [end, start];

    setCurrentDate({
      start,
      end,
      interval: 'ndaysago',
      days: { nStart: _nStart, nEnd: _nEnd },
      nStartTime: newTimeState.nStartTime,
      nEndTime: newTimeState.nEndTime,
    });
  };

  const onChangeNHours = (type: string, value: number) => {
    let _nStart = nHours.nStart ? nHours.nStart : 0;
    let _nEnd = nHours.nEnd ? nHours.nEnd : 0;
    if (type === 'nStart') _nStart = value;
    if (type === 'nEnd') _nEnd = value;
    let start = moment();
    let end = moment();

    start = start.subtract(_nStart, 'hours');
    end = end.subtract(_nEnd, 'hours');

    if (start.isAfter(end)) {
      setCurrentDate({
        start: end,
        end: start,
        interval: 'nhoursago',
        hours: { nStart: _nStart, nEnd: _nEnd },
      });
    } else {
      setCurrentDate({
        start: start,
        end: end,
        interval: 'nhoursago',
        hours: { nStart: _nStart, nEnd: _nEnd },
      });
    }

    setNHours((prevVal) => ({
      ...prevVal,
      [type]: value,
    }));
  };

  const handleRadioChange = (e: any) => {
    onChangeRadio(e.target.value);
  };

  return (
    <>
      {!isFixed && (
        <Radio.Group
          value={currentDate.interval}
          onChange={handleRadioChange}
          buttonStyle="solid"
          disabled={isCompare}
        >
          <Radio.Button value="today">Сегодня</Radio.Button>
          <Radio.Button value="yesterday">Вчера</Radio.Button>
          <Radio.Button value="last7days">7 дней</Radio.Button>
          <Radio.Button value="last14days" disabled={isAccessEnded}>
            {isAccessEnded && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            14 дней
          </Radio.Button>
          <Radio.Button value="last30days" disabled={isAccessEnded}>
            {isAccessEnded && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            30 дней
          </Radio.Button>
        </Radio.Group>
      )}
      {!isFixed && (
        <Select
          style={{ minWidth: 200 }}
          dropdownMatchSelectWidth={false}
          popupClassName="period-select-dropdown"
          className={currentDate.interval === 'ndaysago' || currentDate.interval === 'nhoursago' ? 'period-select-active' : ''}
          optionLabelProp="label"
          value={currentDate.interval}
          placeholder="Выбрать период"
          onSelect={onChangeType}
          showSearch
          filterOption={false}
          disabled={isCompare}
        >
          <Select.Option value="selectdates" label="выбрать даты">выбрать даты</Select.Option>
          <Select.Option value="today" label="сегодня">сегодня</Select.Option>
          <Select.Option value="yesterday" label="вчера">вчера</Select.Option>
          <Select.Option value="thisweek" label="эта неделя">эта неделя</Select.Option>
          <Select.Option value="lastweek" label="прошлая неделя" disabled={isAccessEnded}>
            прошлая неделя
          </Select.Option>
          <Select.Option value="thismonth" label="этот месяц" disabled={isAccessEnded}>
            этот месяц
          </Select.Option>
          <Select.Option value="lastmonth" label="прошлый месяц" disabled={isAccessEnded}>
            прошлый месяц
          </Select.Option>
          <Select.Option value="thisquart" label="этот квартал" disabled={isAccessEnded}>
            этот квартал
          </Select.Option>
          <Select.Option value="lastquart" label="прошлый квартал" disabled={isAccessEnded}>
            прошлый квартал
          </Select.Option>
          <Select.Option value="thisyear" label="этот год" disabled={isAccessEnded}>
            этот год
          </Select.Option>
          <Select.Option value="lastyear" label="прошлый год" disabled={isAccessEnded}>
            прошлый год
          </Select.Option>
          <Select.Option value="last7days" label="последние 7 дней">последние 7 дней</Select.Option>
          <Select.Option value="last14days" label="последние 14 дней" disabled={isAccessEnded}>
            последние 14 дней
          </Select.Option>
          <Select.Option value="last30days" label="последние 30 дней" disabled={isAccessEnded}>
            последние 30 дней
          </Select.Option>
          <Select.Option value="last365days" label="последние 365 дней" disabled={isAccessEnded}>
            последние 365 дней
          </Select.Option>
          <Select.Option value="ndaysago" label={nDaysLabel}>N дней назад</Select.Option>
          <Select.Option value="nhoursago" label={nHoursLabel}>N часов назад</Select.Option>
        </Select>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap' }}>
        <Popover
          content={
            currentDateSelector === 'ndaysago' && isOpenOutside ? (
              <Space size="small" direction="vertical">
                <Space>
                  <Typography.Text type="secondary">от</Typography.Text>
                  <InputNumber
                    disabled={isCompare}
                    value={nDays.nStart}
                    onChange={(val) =>
                      onChangeN('nStart', typeof val === 'number' ? val : 0)
                    }
                    style={{ width: 100 }}
                  />

                  <Typography.Text type="secondary">до</Typography.Text>
                  <InputNumber
                    disabled={isCompare}
                    value={nDays.nEnd}
                    onChange={(val) =>
                      onChangeN('nEnd', typeof val === 'number' ? val : 0)
                    }
                    style={{ width: 100 }}
                  />
                </Space>
                <Space size="small">
                  <Button
                    disabled={isCompare}
                    onClick={onClickNDaysTime}
                    icon={<ClockCircleOutlined />}
                    type={nDaysTime.nStartTime != null ? 'link' : 'text'}
                    size="small"
                  />
                  {nDaysTime.nStartTime != null && (
                    <>
                      <NTP
                        disabled={isCompare}
                        placeholder="с"
                        value={nDaysTime.nStartTime}
                        onChange={(val: any) => onChangeNDaysTime('nStartTime', val)}
                      />
                      <Typography.Text type="secondary">—</Typography.Text>
                      <NTP
                        disabled={isCompare}
                        placeholder="по"
                        value={nDaysTime.nEndTime}
                        onChange={(val: any) => onChangeNDaysTime('nEndTime', val)}
                      />
                    </>
                  )}
                </Space>
                <div>
                  {moment(currentDate.start ?? period.start ?? currentDashboard?.values?.period.start).format(format).toUpperCase()}
                  &nbsp;&nbsp;—&nbsp;&nbsp;
                  {moment(currentDate.end ?? period.end ?? currentDashboard?.values?.period.end).format(format).toUpperCase()}
                </div>
                <Button
                  type="primary"
                  style={{ marginTop: 10 }}
                  disabled={isCompare}
                  onClick={() => onApply()}
                >
                  Применить
                </Button>
              </Space>
            ) : currentDateSelector === 'nhoursago' && isOpenOutside ? (
              <Space size="small" direction="vertical">
                <Space>
                  <Typography.Text type="secondary">от</Typography.Text>
                  <InputNumber
                    disabled={isCompare}
                    value={nHours.nStart}
                    onChange={(val) =>
                      onChangeNHours('nStart', typeof val === 'number' ? val : 0)
                    }
                    style={{ width: 100 }}
                  />

                  <Typography.Text type="secondary">до</Typography.Text>
                  <InputNumber
                    disabled={isCompare}
                    value={nHours.nEnd}
                    onChange={(val) =>
                      onChangeNHours('nEnd', typeof val === 'number' ? val : 0)
                    }
                    style={{ width: 100 }}
                  />
                </Space>
                <div>
                  {moment(currentDate.start ?? period.start ?? currentDashboard?.values?.period.start).format('DD.MM.YYYY HH:mm').toUpperCase()}
                  &nbsp;&nbsp;—&nbsp;&nbsp;
                  {moment(currentDate.end ?? period.end ?? currentDashboard?.values?.period.end).format('DD.MM.YYYY HH:mm').toUpperCase()}
                </div>
                <Button
                  type="primary"
                  style={{ marginTop: 10 }}
                  disabled={isCompare}
                  onClick={() => onApply()}
                >
                  Применить
                </Button>
              </Space>
            ) : (
              <Space size="small" direction="vertical">
                <Space>
                  <Button
                    disabled={isCompare}
                    onClick={() => {
                      const next = !withTime;
                      setWithTime(next);
                      if (next) {
                        const s = moment(currentDate.start ?? period.start).set(startTime);
                        const e = moment(currentDate.end ?? period.end).set(endTime);
                        setCurrentDate((prev: any) => ({ ...prev, start: s, end: e, interval: 'selectdates' }));
                      }
                    }}
                    icon={<ClockCircleOutlined />}
                    type={withTime ? 'link' : 'text'}
                    size="small"
                  />
                  <DP value={currentDate.start ?? period.start ?? currentDashboard?.values?.period.start} type="start" format={format} withTime={withTime} isCompare={isCompare} isAccessEnded={isAccessEnded} onChangeDate={onChangeDate} />
                  <Typography.Text type="secondary">—</Typography.Text>
                  <DP value={currentDate.end ?? period.end ?? currentDashboard?.values?.period.end} type="end" format={format} withTime={withTime} isCompare={isCompare} isAccessEnded={isAccessEnded} onChangeDate={onChangeDate} />
                </Space>

                <Button
                  type="primary"
                  disabled={isCompare}
                  style={{ marginTop: 10 }}
                  onClick={onApply}
                >
                  Применить
                </Button>
              </Space>
            )
          }
          title="Период"
          open={visibleDateModal}
          onOpenChange={(visible) => {
            setVisibleDateModal(visible), setIsOpenOutside(false);
          }}
          trigger="contextMenu"
          placement="bottom"
        >
          <Button onClick={() => setVisibleDateModal(true)}>
            {moment(currentDate.start ?? period.start ?? currentDashboard?.values?.period.start).format(format).toUpperCase()}
            &nbsp;&nbsp;—&nbsp;&nbsp;
            {moment(currentDate.end ?? period.end ?? currentDashboard?.values?.period.end).format(format).toUpperCase()}
          </Button>
        </Popover>

        {!isFixed && <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center' }}>
          <Tooltip
            placement="bottom"
            title={
              <div
                className="page-title-tooltip-text"
                dangerouslySetInnerHTML={{ __html: periodDescription }}
              ></div>
            }
            color="#fff"
            zIndex={9999}
            trigger="click"
            overlayClassName="page-title-tooltip"
          >
            <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 14 }} />
          </Tooltip>
        </span>}
      </div>
    </>
  );
};

export default PeriodSelector;
